/**
 * Who on the other side has read this conversation.
 *
 * The per-SIDE mark on `conversations` already answers "has anyone over there
 * caught up", and it arrives embedded with the conversation list at no extra
 * cost. This adds the names, which only the open thread needs — so it is a
 * separate call rather than something the list pays for on every row.
 *
 * `read_marks_for_conversation` is SECURITY DEFINER because the rows belong to
 * other users and RLS on `conversation_reads` deliberately hides them. It returns
 * the counterparty side only, asserts participation from `auth.uid()`, and never
 * returns an unread count.
 */
import { useQuery } from '@tanstack/react-query';
import { rpc } from '@/lib/supabase/rpc';
import { isSupabaseConfigured } from '@/lib/supabase';
import { mode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';
import type { CounterpartyMark } from './read-receipt';

export const readMarkKeys = {
  forConversation: (conversationId: string) => ['read-marks', conversationId] as const,
};

export function useConversationReadMarks(conversationId: string | undefined) {
  const query = useQuery({
    queryKey: readMarkKeys.forConversation(conversationId ?? 'none'),
    queryFn: async (): Promise<CounterpartyMark[]> => {
      const { data, error } = await rpc('read_marks_for_conversation', {
        p_conversation_id: conversationId!,
      });
      // A failed read is not an empty read. Surfaced as a warning rather than
      // thrown to the user: the receipt degrades to "Read"/"Sent" from the side
      // mark, which is still correct — only the name is lost.
      if (error) {
        reportError(error, { context: 'useConversationReadMarks', level: 'warning' });
        throw error;
      }
      return (data ?? []).map((row) => ({
        displayName: row.display_name ?? null,
        lastReadAt: row.last_read_at,
      }));
    },
    enabled: !mode.isMock && !!conversationId && isSupabaseConfigured(),
    // Receipts are ambient, not transactional. The conversations subscription
    // already pushes the side mark, which is what flips Sent -> Read; these names
    // can lag a little without the label ever being wrong.
    staleTime: 30 * 1000,
  });

  return query.data ?? [];
}
